import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import {
  CatalogRequest,
  ILocationInfo,
  ISummaryData,
} from '../../../../../core/models/summary-data';
import { SummaryService } from '../../../../../core/services/summary.service';
import { FormGroup } from '@angular/forms';
import { FunnelService } from '../funnel.service';
import { NgZone } from '@angular/core';
import { isSame } from 'ngx-bootstrap/chronos';
import { finalize } from 'rxjs';
import { LoaderService } from '../../../../../core/services/loader.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-pickup',
  standalone: false,
  templateUrl: './pickup.component.html',
  styleUrl: './pickup.component.scss',
})
export class PickupComponent implements AfterViewInit {

  @Input() form!: FormGroup;
  @Output() productCatalogResponseEvent = new EventEmitter<any>();
  @Output() locationDataCompleted = new EventEmitter<ILocationInfo>();

  @ViewChild('originInput') originInput!: ElementRef<HTMLInputElement>;
  @ViewChild('destinationInput') destinationInput?: ElementRef<HTMLInputElement>;

  pickupAddressIsTheSame = true;

  originCoords = { lat: 0, lng: 0 };
  destinationCoords = { lat: 0, lng: 0 };

  originAddressFormatted = '';
  destinationAddressFormatted = '';
  catalogRequest: CatalogRequest = {} as CatalogRequest;
  summaryData: ISummaryData = {} as ISummaryData;
  productCatalogResponse: any = {} as any;
  brandingId = '';
  placeholderInput = '';
  placeholderExtra = '';

  private addressAutocompleteInitialized = false;

  constructor(private ngZone: NgZone, private funnelService: FunnelService, private summaryService: SummaryService,
    private loaderService: LoaderService, private translate: TranslateService
  ) {
    this.funnelService.getBrandingStyles(this.brandingId).subscribe((data) => {
      console.log('Branding styles:', data);
    
      document.documentElement.style.setProperty('--background-color', data.BackgroundColor);
      document.documentElement.style.setProperty('--text-color', data.TextColor);
      document.documentElement.style.setProperty('--button-text-color', data.ButtonTextColor);
    
      if (data.ButtonColor.length > 1) {
        document.documentElement.style.setProperty('--button-gradient-start', data.ButtonColor[0]);
        document.documentElement.style.setProperty('--button-gradient-middle', data.ButtonColor[1]);
        document.documentElement.style.setProperty('--button-gradient-end', data.ButtonColor[2]);
      } else {
        document.documentElement.style.setProperty('--button-gradient-start', data.ButtonColor[0]);
        document.documentElement.style.setProperty('--button-gradient-end', data.ButtonColor[0]);
      }
    });
  }

  ngOnInit(): void {
    this.setPlaceholders(); // inicializa según el idioma actual

    this.translate.onLangChange.subscribe(() => {
      this.setPlaceholders(); // se actualiza cuando cambia el idioma
    });
    
    this.summaryData = this.summaryService.getSummary() as ISummaryData;
    console.log(this.summaryData);
  }

  ngAfterViewInit(): void {
    this.initOriginAutocomplete();
  }

  ngAfterViewChecked(): void {
    if (
      !this.pickupAddressIsTheSame &&
      this.destinationInput &&
      !this.addressAutocompleteInitialized
    ) {
      this.initDestinationAutocomplete();
    }
  }

  togglePickupAddressIsTheSame(): void {
    this.pickupAddressIsTheSame = !this.pickupAddressIsTheSame;

    if (this.pickupAddressIsTheSame) {
      this.destinationCoords = { ...this.originCoords };
      this.destinationAddressFormatted = this.originAddressFormatted;
      this.form.get('destinationAddress')?.setValue(this.originAddressFormatted);
    } else {
      this.addressAutocompleteInitialized = false;
      this.destinationCoords = { lat: 0, lng: 0 };
      this.destinationAddressFormatted = '';
      this.form.get('destinationAddress')?.reset();
      const input = this.destinationInput?.nativeElement;
      if (input) input.value = '';
    }

    this.updateAddressValidators();
  }

  updateAddressValidators(): void {
    const destCtrl = this.form.get('destinationAddress');
    if (!destCtrl) return;

    if (this.pickupAddressIsTheSame) {
      destCtrl.clearValidators();
    } else {
      destCtrl.setValidators([]);
    }
    destCtrl.updateValueAndValidity();
  }

  isFormValid(): boolean {
    const originOk =
      !!this.originCoords.lat &&
      !!this.originCoords.lng &&
      this.originAddressFormatted.trim().length > 0;

    const destinationOk =
      !!this.destinationCoords.lat &&
      !!this.destinationCoords.lng &&
      this.destinationAddressFormatted.trim().length > 0;

    return this.pickupAddressIsTheSame ? originOk : originOk && destinationOk;
  }

  onOriginInputChange(): void {
    const value = this.form.get('originAddress')?.value ?? '';
    if (value.trim() === '') {
      this.originCoords = { lat: 0, lng: 0 };
      this.originAddressFormatted = '';
      if (this.pickupAddressIsTheSame) {
        this.destinationCoords = { lat: 0, lng: 0 };
        this.destinationAddressFormatted = '';
        this.form.get('destinationAddress')?.setValue('');
      }
    }
  }
  
  onDestinationInputChange(): void {
    if (this.pickupAddressIsTheSame) return;
  
    const value = this.form.get('destinationAddress')?.value ?? '';
    if (value.trim() === '') {
      this.destinationCoords = { lat: 0, lng: 0 };
      this.destinationAddressFormatted = '';
    }
  }

  initOriginAutocomplete(): void {
    const autocomplete = new google.maps.places.Autocomplete(this.originInput.nativeElement, {
      types: ['address'],
      componentRestrictions: { country: 'es' }
    });

    autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place = autocomplete.getPlace();
        const location = place.geometry?.location;
        const address = place.formatted_address || '';

        if (location) {
          this.originCoords = {
            lat: location.lat(),
            lng: location.lng()
          };
          this.originAddressFormatted = address;

          if (this.pickupAddressIsTheSame) {
            this.destinationCoords = { ...this.originCoords };
            this.destinationAddressFormatted = address;
            this.form.get('destinationAddress')?.setValue(address);
          }

          this.patchFormWithAddresses();
        }
      });
    });
  }

  initDestinationAutocomplete(): void {
    const autocomplete = new google.maps.places.Autocomplete(
      this.destinationInput!.nativeElement,
      {
        types: ['address'],
        componentRestrictions: { country: 'es' }
      }
    );

    autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place = autocomplete.getPlace();
        const location = place.geometry?.location;
        const address = place.formatted_address || '';

        if (location) {
          this.destinationCoords = {
            lat: location.lat(),
            lng: location.lng()
          };
          this.destinationAddressFormatted = address;
          this.patchFormWithAddresses();
        }
      });
    });

    this.addressAutocompleteInitialized = true;
  }

  patchFormWithAddresses(): void {
    const origin = this.originCoords;
    const destination = this.pickupAddressIsTheSame ? origin : this.destinationCoords;

    const originFinal = this.originAddressFormatted;
    const destinationFinal = this.pickupAddressIsTheSame
      ? this.originAddressFormatted
      : this.destinationAddressFormatted;

    this.form.patchValue({
      isSameAddress: this.pickupAddressIsTheSame,
      originAddress: originFinal,
      originAddressLatitude: origin.lat,
      originAddressLongitude: origin.lng,
      destinationAddress: destinationFinal,
      destinationAddressLatitude: destination.lat,
      destinationAddressLongitude: destination.lng
    });

    this.catalogRequest = {
      originLatitude: origin.lat,
      originLongitude: origin.lng,
      destinationLatitude: destination.lat,
      destinationLongitude: destination.lng,
      chassisType: this.summaryData.vehicleData!.vehicleType,
      fuelType: this.summaryData.vehicleData!.fuelType,
    };

  }

    handleNext() {
      this.loaderService.show();
    this.funnelService
      .getProductCatalog(this.catalogRequest).pipe(
              finalize(() => this.loaderService.hide()),
            )
      .subscribe((data) => {
        this.productCatalogResponse = data;
        this.productCatalogResponseEvent.emit(this.productCatalogResponse);
        this.locationDataCompleted.emit(this.form.value);
      });
  }

  private setPlaceholders(): void {
    this.translate.get('placeholder-input').subscribe(text => {
      this.placeholderInput = text;
    });
  
    this.translate.get('placeholder-extra').subscribe(text => {
      this.placeholderExtra = text;
    });
  }

}



